import React, { FC, useContext, useState } from "react";
import { IPreferences, KeyAuditCriteria, KeyAuditType } from "../../logic/api";
import { GridLegacy as Grid } from "@mui/material";
import NotchedCard from "../../components/cards/NotchedCard/NotchedCard";
import EditableGroup, { EditableGroupType } from "../../components/editable/EditableGroup/EditableGroup";
import styles from "./styles";
import { SessionContext } from "../../utility/useSession";
import EditableField, { EditableFieldTypes } from "../../components/editable/EditableField/EditableField";
import { useTranslation } from "react-i18next";
import { IDropDownListItem } from "@/components/editable/EditableField/fields/DropDownField";

export interface IKeyAuditSettingsProps {
  onSave: (newPrefs: Partial<IPreferences>) => Promise<boolean>;
}

export const KeyAuditSettings: FC<IKeyAuditSettingsProps> = ({
  onSave,
}) => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();

  const preferences = session.current.site?.preferences;
  const keyAuditCriteria = preferences?.key_audit_criteria;

  if (!preferences || !keyAuditCriteria) {
    return null;
  }

  const [laneCriteria, setLaneCriteria] = useState<KeyAuditCriteria[]>(keyAuditCriteria?.lane.criteria ?? [KeyAuditCriteria.AnyMovement]);
  const [standaloneCriteria, setStandaloneCriteria] = useState<KeyAuditCriteria[]>(keyAuditCriteria?.standalone.criteria ?? [KeyAuditCriteria.AnyMovement]);

  const getAuditCriteriaDropDownOptions = (isLaneLocation: boolean) => {
    const items: IDropDownListItem[] = [
      { name: t("settings.keyAudits.criteria.AnyMovement"), value: KeyAuditCriteria.AnyMovement.toString() },
      { name: t("settings.keyAudits.criteria.24HoursNoMovement"), value: KeyAuditCriteria["24HoursNoMovement"].toString() },
      { name: t("settings.keyAudits.criteria.FullBox"), value: KeyAuditCriteria.FullBox.toString() },
      { name: t("settings.keyAudits.criteria.DoNotAudit"), value: KeyAuditCriteria.DoNotAudit.toString() },
    ];

    if (isLaneLocation) {
      items.splice(3, 0, { name: t("settings.keyAudits.criteria.LaneAuditComplete"), value: KeyAuditCriteria.LaneAuditComplete.toString() });
    }

    return items;
  }

  const getAuditTypeDropDownOptions = (hasOrder: boolean) => {
    const items: IDropDownListItem[] = [
      { name: t("settings.keyAudits.type.Contents"), value: KeyAuditType.Contents.toString() },
    ]

    if (preferences.enable_enforce_key_position_audit && hasOrder === true) {
      items.push({ name: t("settings.keyAudits.type.Order"), value: KeyAuditType.Order.toString() });
    }

    return items;
  }

  return <Grid item xs={12}>
    <NotchedCard label={t("settings.keyAudits.title")} style={styles.card}>
      <EditableGroup<IPreferences>
        id="devices"
        testId="key-audit-settings-group"
        editType={EditableGroupType.switchableEditing}
        data={preferences}
        onDataChanged={(data) => {
          return onSave({
            key_audit_criteria: data.key_audit_criteria
          })
        }}
      >
        <Grid>

          <p style={{ marginBottom: 0 }}><strong>{t(`settings.keyAudits.lane`)}</strong></p>
          <Grid container>
            <Grid item sm={6} xs={6}>
              <EditableField<IPreferences, IDropDownListItem>
                useFullWidthWhenViewing={true}
                useFullWidthWhenEditing={false}
                inputType={EditableFieldTypes.enumDropDownField}
                label={t("settings.keyAudits.criteria.label")}
                onProvide={(data) => ({
                  name: data.key_audit_criteria ? t(`settings.keyAudits.criteria.${KeyAuditCriteria[data.key_audit_criteria?.lane.criteria[0]].toString()}`) : "",
                  value: data.key_audit_criteria ? data.key_audit_criteria?.lane.criteria[0].toString() : ""
                })}
                onRequestData={async () => getAuditCriteriaDropDownOptions(true)}
                onRequestText={(data) => data.key_audit_criteria ? t(`settings.keyAudits.criteria.${KeyAuditCriteria[data.key_audit_criteria?.lane.criteria[0]].toString()}`) : ""}
                onChange={(data, value) => {
                  if (data.key_audit_criteria && value.value) {
                    data.key_audit_criteria.lane.criteria = [Number(value.value)];
                    setLaneCriteria([Number(value.value)]);
                  }
                }}
                testId={`key-audit-settings-lane-criteria`}
              />
            </Grid>

            {laneCriteria[0] !== KeyAuditCriteria.DoNotAudit &&
              <Grid item sm={6} xs={6}>
                <EditableField<IPreferences, IDropDownListItem>
                  useFullWidthWhenViewing={true}
                  useFullWidthWhenEditing={false}
                  inputType={EditableFieldTypes.enumDropDownField}
                  label={t("settings.keyAudits.type.label")}
                  onProvide={(data) => ({
                    name: data.key_audit_criteria ? t(`settings.keyAudits.type.${KeyAuditType[data.key_audit_criteria?.lane.auditType].toString()}`) : "",
                    value: data.key_audit_criteria ? data.key_audit_criteria?.lane.auditType.toString() : ""
                  })}
                  onRequestData={async () => getAuditTypeDropDownOptions(true)}
                  onChange={(data, value) => {
                    if (data.key_audit_criteria) {
                      data.key_audit_criteria.lane.auditType = Number(value.value) as KeyAuditType;
                    }
                  }}
                  testId={`key-audit-settings-lane-audit-type`}
                />
              </Grid>
            }
          </Grid>
          <p style={{ marginTop: 40, marginBottom: 0 }}><strong>{t(`settings.keyAudits.standalone`)}</strong></p>
          <Grid container>
            <Grid item sm={6} xs={6}>
              <EditableField<IPreferences, IDropDownListItem>
                useFullWidthWhenViewing={true}
                useFullWidthWhenEditing={false}
                inputType={EditableFieldTypes.enumDropDownField}
                label={t("settings.keyAudits.criteria.label")}
                onProvide={(data) => {
                  return {
                    name: data.key_audit_criteria ? t(`settings.keyAudits.criteria.${KeyAuditCriteria[data.key_audit_criteria?.standalone.criteria[0]].toString()}`) : "",
                    value: data.key_audit_criteria ? data.key_audit_criteria?.standalone.criteria[0].toString() : ""
                  } as IDropDownListItem;
                }}
                onRequestData={async () => getAuditCriteriaDropDownOptions(false)}
                onRequestText={(data) => data.key_audit_criteria ? t(`settings.keyAudits.criteria.${KeyAuditCriteria[data.key_audit_criteria?.standalone.criteria[0]].toString()}`) : ""}
                onChange={(data, value) => {
                  if (data.key_audit_criteria) {
                    data.key_audit_criteria.standalone.criteria = [Number(value.value)]
                    setStandaloneCriteria([Number(value.value)]);
                  }
                }}
                testId={`key-audit-settings-standalone-criteria`}
              />
            </Grid>


            {standaloneCriteria[0] !== KeyAuditCriteria.DoNotAudit &&
              <Grid item sm={6} xs={6}>
                <EditableField<IPreferences, IDropDownListItem>
                  useFullWidthWhenViewing={true}
                  useFullWidthWhenEditing={false}
                  inputType={EditableFieldTypes.enumDropDownField}
                  label={t("settings.keyAudits.type.label")}
                  readOnly={true}
                  onProvide={(data) => ({
                    name: data.key_audit_criteria ? t(`settings.keyAudits.type.${KeyAuditType[data.key_audit_criteria?.standalone.auditType].toString()}`) : "",
                    value: data.key_audit_criteria ? data.key_audit_criteria?.standalone.auditType.toString() : ""
                  })}
                  onRequestData={async () => getAuditTypeDropDownOptions(false)}
                  onChange={(data, value) => {
                    if (data.key_audit_criteria) {
                      data.key_audit_criteria.standalone.auditType = Number(value.value) as KeyAuditType;
                    }
                  }}
                  testId={`key-audit-settings-standalone-audit-type`}
                />
              </Grid>
            }
          </Grid>

        </Grid>
      </EditableGroup>
    </NotchedCard>
  </Grid>
};
